import { InfoIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Column and tile names shared across the page analytics screens, so the same
 * metric reads the same everywhere (leads ÷ sessions is always "Conversion
 * rate", never CVR or lead rate).
 */
export const METRIC_LABEL = {
  conversionRate: 'Conversion rate',
  paidConversionRate: 'Paid conversion rate',
  leadsAndConversionRate: 'Leads · conversion rate',
  firstSeen: 'First seen',
  lastSeen: 'Last seen',
} as const;

/** Plain-English definitions for the search and traffic metrics, shown on hover. */
export const METRIC_INFO = {
  clicks: 'Times someone clicked through to the site from Google search results.',
  impressions: 'Times a page showed up in Google search results, whether or not it was clicked.',
  ctr: 'Click-through rate: clicks divided by impressions, the share of searchers who clicked.',
  position: 'Average ranking spot in Google results. 1 is the top of the page; lower numbers are better.',
  sessions:
    "One visit to the site: a person's continuous activity in a single sitting. Google Analytics groups their page views and actions into a session, which ends after about 30 minutes of inactivity.",
  leads: 'Form submissions that happened on this page. The visitor converted here.',
  assisted:
    'Leads whose first visit to the site started on this page, even if they submitted the form on another page. First-touch data is only present for some leads, so this undercounts.',
  leadRate:
    'Conversion rate: leads divided by sessions. The share of visits to this page that became a lead here.',
  paidLeadRate:
    'Paid conversion rate: Google Ads leads divided by Google Ads sessions. Sessions are visits Google Analytics credits to google / cpc; leads are the ones that came in with a Google Ads click ID.',
  assistedRate:
    'Assisted rate: assisted leads divided by sessions. The share of visits that started on this page and later became a lead somewhere on the site.',
  change: 'Change against the same number of days just before this range.',
  positionChange:
    'How the average position moved against the same number of days just before this range. Lower is better, so a green down arrow means the page climbed in Google and a red up arrow means it slipped.',
  firstSeen:
    'The first day Google Analytics or Search Console recorded this page. Pages already live when our records begin show that first date, "or earlier".',
  lastSeen:
    'The most recent day Google Analytics or Search Console recorded this page. An old date means the page stopped getting visits or search impressions.',
} as const;

/** A small info icon that reveals a definition on hover or keyboard focus. */
export function InfoTip({ text, label }: { readonly text: string; readonly label: string }): React.ReactElement {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* The ::after pad widens the tap target to about 38px without moving the 14px icon. */}
          <button
            type="button"
            aria-label={`What ${label} means`}
            className="relative inline-flex cursor-help align-middle text-portal-muted after:absolute after:-inset-3 after:content-[''] transition-colors hover:text-portal-ink focus-visible:text-portal-ink focus-visible:outline-none"
          >
            <InfoIcon aria-hidden className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs font-normal normal-case">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
